/**
 * ListPageSkeleton — animated shimmer loading state for listing pages.
 *
 * Usage:
 *   <ListPageSkeleton />                     — table rows (default)
 *   <ListPageSkeleton variant="cards" />     — card grid
 *   <ListPageSkeleton variant="list"  />     — horizontal list rows
 *   <ListPageSkeleton rows={8} />            — custom row count
 */

import React from "react";
import { cn } from "../../utils/cn";

// ── Base shimmer atom ──────────────────────────────────────────────────────────
const Shimmer = ({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) => (
  <div
    className={cn("animate-pulse rounded-md bg-border", className)}
    style={style}
  />
);

// ── Table variant ──────────────────────────────────────────────────────────────

/** Column widths: each row gets slightly randomized cols for realism */
const COL_SETS = [
  ["w-6", "w-36", "w-24", "w-20", "w-16"],
  ["w-6", "w-44", "w-20", "w-28", "w-12"],
  ["w-6", "w-28", "w-32", "w-16", "w-20"],
  ["w-6", "w-40", "w-18", "w-24", "w-14"],
  ["w-6", "w-32", "w-28", "w-20", "w-16"],
  ["w-6", "w-48", "w-16", "w-24", "w-12"],
];

const TableRowSkeleton = ({ index }: { index: number }) => {
  const cols = COL_SETS[index % COL_SETS.length];
  return (
    <div className="flex items-center gap-4 px-5 py-4 border-b border-border last:border-0">
      {/* Checkbox / index */}
      <Shimmer className={cn("h-4 shrink-0", cols[0])} />
      {/* Main content */}
      <div className="flex-1 flex items-center gap-6 min-w-0">
        <Shimmer className={cn("h-3.5 shrink-0", cols[1])} />
        <Shimmer className={cn("h-3 shrink-0", cols[2])} />
        <Shimmer className={cn("h-3 shrink-0", cols[3])} />
        <div className="flex-1" />
        {/* Badge */}
        <Shimmer className={cn("h-5 rounded-full shrink-0", cols[4])} />
      </div>
      {/* Action buttons */}
      <div className="flex items-center gap-2 shrink-0">
        <Shimmer className="h-7 w-7 rounded-md" />
        <Shimmer className="h-7 w-7 rounded-md" />
      </div>
    </div>
  );
};

const TableSkeleton = ({ rows }: { rows: number }) => (
  <div className="rounded-xl border border-border bg-card overflow-hidden">
    {/* Table header */}
    <div className="flex items-center gap-4 px-5 py-3 bg-surface border-b border-border">
      <Shimmer className="h-3 w-4" />
      <Shimmer className="h-3 w-24" />
      <Shimmer className="h-3 w-16 ml-6" />
      <Shimmer className="h-3 w-20 ml-6" />
      <div className="flex-1" />
      <Shimmer className="h-3 w-14" />
    </div>
    {/* Rows */}
    {Array.from({ length: rows }).map((_, i) => (
      <TableRowSkeleton key={i} index={i} />
    ))}
  </div>
);

// ── Card variant ───────────────────────────────────────────────────────────────

const CardSkeleton = ({ index }: { index: number }) => (
  <div className="rounded-xl border border-border bg-card p-5 space-y-4">
    {/* Card header */}
    <div className="flex items-start justify-between gap-3">
      <div className="space-y-2 flex-1">
        <Shimmer className={cn("h-4", index % 3 === 0 ? "w-3/4" : index % 3 === 1 ? "w-2/3" : "w-4/5")} />
        <Shimmer className="h-3 w-1/2" />
      </div>
      <Shimmer className="h-6 w-16 rounded-full" />
    </div>
    {/* Card body */}
    <div className="space-y-2.5">
      <Shimmer className="h-3 w-full" />
      <Shimmer className={cn("h-3", index % 2 === 0 ? "w-5/6" : "w-4/6")} />
    </div>
    {/* Card footer */}
    <div className="flex items-center justify-between pt-1 border-t border-border">
      <Shimmer className="h-3 w-24" />
      <Shimmer className="h-7 w-20 rounded-md" />
    </div>
  </div>
);

const CardGridSkeleton = ({ count }: { count: number }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
    {Array.from({ length: count }).map((_, i) => (
      <CardSkeleton key={i} index={i} />
    ))}
  </div>
);

// ── List variant (horizontal rows, simpler than table) ─────────────────────────

const LIST_WIDTHS = ["w-40", "w-32", "w-48", "w-36", "w-44", "w-28"];

const ListRowSkeleton = ({ index }: { index: number }) => (
  <div className="flex items-center gap-4 px-5 py-4 border-b border-border last:border-0">
    {/* Avatar / icon */}
    <Shimmer className="h-9 w-9 rounded-full shrink-0" />
    {/* Content */}
    <div className="flex-1 space-y-1.5 min-w-0">
      <Shimmer className={cn("h-3.5", LIST_WIDTHS[index % LIST_WIDTHS.length])} />
      <Shimmer className="h-3 w-24" />
    </div>
    {/* Meta */}
    <Shimmer className="h-5 w-20 rounded-full shrink-0" />
    <Shimmer className="h-3 w-16 shrink-0" />
    {/* Actions */}
    <Shimmer className="h-7 w-7 rounded-md shrink-0" />
  </div>
);

const ListRowsSkeleton = ({ rows }: { rows: number }) => (
  <div className="rounded-xl border border-border bg-card overflow-hidden">
    {Array.from({ length: rows }).map((_, i) => (
      <ListRowSkeleton key={i} index={i} />
    ))}
  </div>
);

// ── Stats bar skeleton (for pages with summary stats at top) ───────────────────

const statsBarGridClass = (count: number): string => {
  const n = Math.min(Math.max(count, 1), 6);
  const map: Record<number, string> = {
    1: "grid grid-cols-1 gap-4",
    2: "grid grid-cols-1 sm:grid-cols-2 gap-4",
    3: "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4",
    4: "grid grid-cols-2 md:grid-cols-4 gap-4",
    5: "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4",
    6: "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4",
  };
  return map[n] ?? map[4];
};

export const StatsBarSkeleton = ({ count = 4 }: { count?: number }) => (
  <div className={statsBarGridClass(count)}>
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="rounded-xl border border-border bg-card p-5 space-y-3">
        <div className="flex items-center justify-between">
          <Shimmer className="h-3 w-24" />
          <Shimmer className="h-8 w-8 rounded-lg" />
        </div>
        <Shimmer className="h-7 w-32" />
        <Shimmer className="h-3 w-20" />
      </div>
    ))}
  </div>
);

// ── Filter bar skeleton ────────────────────────────────────────────────────────

export const FilterBarSkeleton = () => (
  <div className="flex items-center gap-3 flex-wrap">
    <Shimmer className="h-9 flex-1 min-w-[200px] max-w-xs" />
    <Shimmer className="h-9 w-32" />
    <Shimmer className="h-9 w-32" />
    <div className="flex-1" />
    <Shimmer className="h-9 w-28" />
  </div>
);

// ── Kanban variant ─────────────────────────────────────────────────────────────

const KanbanCardSkeleton = () => (
  <div className="rounded-lg border border-border bg-card p-3 space-y-2.5">
    <div className="flex items-center gap-2">
      <Shimmer className="h-6 w-6 rounded-full shrink-0" />
      <Shimmer className="h-3.5 flex-1" />
    </div>
    <Shimmer className="h-3 w-4/5" />
    <Shimmer className="h-3 w-3/5" />
    <div className="flex items-center justify-between pt-1 border-t border-border">
      <Shimmer className="h-3 w-16" />
      <Shimmer className="h-3 w-12" />
    </div>
  </div>
);

export const KanbanSkeleton = ({ columns = 4, cardsPerColumn = 3 }: { columns?: number; cardsPerColumn?: number }) => (
  <div className="flex gap-4 overflow-x-auto pb-2">
    {Array.from({ length: columns }).map((_, col) => (
      <div key={col} className="flex-shrink-0 w-64 space-y-3">
        {/* Column header */}
        <div className="flex items-center justify-between px-1">
          <div className="flex items-center gap-2">
            <Shimmer className="h-2.5 w-2.5 rounded-full" />
            <Shimmer className="h-3.5 w-24" />
          </div>
          <Shimmer className="h-5 w-6 rounded-full" />
        </div>
        {/* Cards */}
        <div className="space-y-2">
          {Array.from({ length: cardsPerColumn - (col % 2) }).map((_, i) => (
            <KanbanCardSkeleton key={i} />
          ))}
        </div>
      </div>
    ))}
  </div>
);

// ── Settings page skeleton ─────────────────────────────────────────────────────

export const SettingsPageSkeleton = () => (
  <div className="space-y-6">
    {/* Header */}
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <div className="space-y-2">
        <Shimmer className="h-7 w-56" />
        <Shimmer className="h-4 w-72" />
      </div>
      <div className="flex gap-2">
        <Shimmer className="h-9 w-36 rounded-lg" />
        <Shimmer className="h-9 w-36 rounded-lg" />
      </div>
    </div>
    {/* Info box */}
    <Shimmer className="h-14 w-full rounded-lg" />
    {/* Main card */}
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="flex items-center justify-between px-6 py-4 border-b border-border">
        <Shimmer className="h-5 w-40" />
        <Shimmer className="h-8 w-32 rounded-lg" />
      </div>
      <div className="p-6 space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 p-3 rounded-lg border border-border">
            <Shimmer className="h-5 w-5 rounded shrink-0" />
            <Shimmer className="h-2.5 w-2.5 rounded-full shrink-0" />
            <Shimmer className={cn("h-4", i % 3 === 0 ? "w-24" : i % 3 === 1 ? "w-32" : "w-20")} />
            <div className="flex-1" />
            <Shimmer className="h-6 w-16 rounded-md" />
            <Shimmer className="h-7 w-7 rounded-md" />
            <Shimmer className="h-7 w-7 rounded-md" />
          </div>
        ))}
      </div>
    </div>
    {/* Suggestions card */}
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="px-6 py-4 border-b border-border">
        <Shimmer className="h-5 w-48" />
      </div>
      <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="rounded-lg border border-border p-4 space-y-2">
            <Shimmer className="h-4 w-3/5" />
            <Shimmer className="h-3 w-4/5" />
            <div className="flex gap-1 pt-1">
              {Array.from({ length: 4 }).map((__, j) => (
                <Shimmer key={j} className="h-5 w-14 rounded-full" />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export const DetailPageSkeleton: React.FC = () => (
  <div className="space-y-6">
    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
      <div className="flex items-center gap-4">
        <Shimmer className="h-9 w-24 rounded-md shrink-0" />
        <div className="space-y-2">
          <Shimmer className="h-8 w-48 md:w-72 max-w-full" />
          <Shimmer className="h-4 w-40 max-w-full" />
        </div>
      </div>
      <div className="flex gap-2 shrink-0">
        <Shimmer className="h-9 w-28 rounded-md" />
        <Shimmer className="h-9 w-28 rounded-md" />
      </div>
    </div>
    <div className="rounded-xl border border-border bg-card p-6 space-y-4">
      <Shimmer className="h-6 w-52 max-w-full" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Shimmer className="h-24 rounded-lg" />
        <Shimmer className="h-24 rounded-lg" />
        <Shimmer className="h-24 rounded-lg" />
      </div>
      <Shimmer className="h-40 w-full rounded-lg" />
    </div>
  </div>
);

const ProposalDetailSectionSkeleton = () => (
  <div className="rounded-xl border border-border bg-card overflow-hidden">
    <div className="px-6 py-4 border-b border-border flex items-center gap-2">
      <Shimmer className="h-5 w-5 rounded shrink-0" />
      <Shimmer className="h-5 w-36" />
    </div>
    <div className="p-6 space-y-3">
      <Shimmer className="h-6 w-56 max-w-full" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Shimmer className="h-4 w-full" />
        <Shimmer className="h-4 w-full" />
        <Shimmer className="h-4 w-4/5" />
        <Shimmer className="h-4 w-3/5" />
      </div>
      <Shimmer className="h-9 w-44 rounded-md" />
    </div>
  </div>
);

export const ProposalDetailsPageSkeleton: React.FC = () => (
  <div className="space-y-6 max-w-7xl mx-auto">
    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
      <div className="flex items-start gap-4">
        <Shimmer className="h-9 w-24 rounded-md shrink-0" />
        <div className="space-y-2 min-w-0">
          <Shimmer className="h-8 w-52 md:w-64 max-w-full" />
          <Shimmer className="h-4 w-48 max-w-full" />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 lg:gap-3">
        <Shimmer className="h-8 w-36 rounded-full" />
        <Shimmer className="h-9 w-24 rounded-md" />
        <Shimmer className="h-9 w-24 rounded-md" />
        <Shimmer className="h-9 w-24 rounded-md" />
      </div>
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <ProposalDetailSectionSkeleton />
        <ProposalDetailSectionSkeleton />
        <ProposalDetailSectionSkeleton />
      </div>
      <div className="space-y-6">
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="px-6 py-4 border-b border-border space-y-2">
            <Shimmer className="h-10 w-40" />
            <Shimmer className="h-4 w-32" />
          </div>
          <div className="p-6 space-y-3">
            <Shimmer className="h-4 w-full" />
            <Shimmer className="h-4 w-full" />
            <div className="pt-3 border-t border-border space-y-2">
              <Shimmer className="h-4 w-3/4" />
              <Shimmer className="h-8 w-full rounded-lg" />
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-border bg-card p-6 space-y-3">
          <Shimmer className="h-5 w-28" />
          <Shimmer className="h-4 w-full" />
          <Shimmer className="h-4 w-5/6" />
        </div>
      </div>
    </div>
  </div>
);

export const ProposalEditPageSkeleton: React.FC = () => (
  <div className="space-y-6 max-w-7xl mx-auto">
    <div className="flex items-center gap-4">
      <Shimmer className="h-9 w-24 rounded-md shrink-0" />
      <div className="space-y-2">
        <Shimmer className="h-8 w-48 max-w-full" />
        <Shimmer className="h-4 w-64 max-w-full" />
      </div>
    </div>
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="px-6 py-4 border-b border-border">
        <Shimmer className="h-6 w-40" />
      </div>
      <div className="p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <Shimmer className="h-3 w-24" />
              <Shimmer className="h-10 w-full rounded-md" />
            </div>
          ))}
        </div>
        <div className="space-y-2">
          <Shimmer className="h-3 w-28" />
          <Shimmer className="h-24 w-full rounded-md" />
        </div>
        <div className="flex flex-wrap gap-3 pt-2">
          <Shimmer className="h-9 w-32 rounded-md" />
          <Shimmer className="h-9 w-28 rounded-md" />
          <Shimmer className="h-9 w-36 rounded-md" />
        </div>
      </div>
    </div>
  </div>
);

export const VehicleDetailsPageSkeleton: React.FC = () => (
  <div className="max-w-7xl mx-auto space-y-5">
    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
      <div className="flex items-start gap-4">
        <Shimmer className="h-8 w-20 rounded-md shrink-0" />
        <div className="space-y-2 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Shimmer className="h-7 w-56 max-w-full" />
            <Shimmer className="h-6 w-24 rounded-full" />
          </div>
          <Shimmer className="h-4 w-48 max-w-full" />
        </div>
      </div>
      <div className="flex gap-2 shrink-0">
        <Shimmer className="h-8 w-24 rounded-md" />
        <Shimmer className="h-8 w-24 rounded-md" />
      </div>
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      <div className="lg:col-span-2 space-y-5">
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <Shimmer className="aspect-video w-full rounded-none" />
          <div className="p-4 flex justify-center gap-2">
            <Shimmer className="h-8 w-8 rounded-md" />
            <Shimmer className="h-8 w-8 rounded-md" />
          </div>
        </div>
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="px-6 py-4 border-b border-border flex items-center gap-2">
            <Shimmer className="h-5 w-5 rounded shrink-0" />
            <Shimmer className="h-5 w-32" />
          </div>
          <div className="p-6 space-y-3">
            <Shimmer className="h-5 w-3/4" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Shimmer className="h-4 w-full" />
              <Shimmer className="h-4 w-full" />
              <Shimmer className="h-4 w-5/6" />
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="px-6 py-4 border-b border-border flex items-center gap-2">
            <Shimmer className="h-5 w-5 rounded shrink-0" />
            <Shimmer className="h-5 w-36" />
          </div>
          <div className="p-6 space-y-2">
            <Shimmer className="h-4 w-full" />
            <Shimmer className="h-4 w-full" />
            <Shimmer className="h-4 w-2/3" />
          </div>
        </div>
      </div>
      <div className="space-y-5">
        <div className="rounded-xl border border-border bg-card p-6 space-y-4">
          <Shimmer className="h-8 w-36" />
          <Shimmer className="h-4 w-full" />
          <div className="pt-3 border-t border-border space-y-2">
            <Shimmer className="h-4 w-full" />
            <Shimmer className="h-6 w-full rounded-md" />
          </div>
        </div>
        <div className="rounded-xl border border-border bg-card p-6 space-y-3">
          <Shimmer className="h-5 w-28" />
          <Shimmer className="h-4 w-full" />
          <Shimmer className="h-4 w-4/5" />
        </div>
      </div>
    </div>
  </div>
);

export const VehicleEditPageSkeleton: React.FC = () => (
  <div className="max-w-5xl mx-auto space-y-6">
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <div className="flex items-center space-x-4">
        <Shimmer className="h-9 w-24 rounded-md shrink-0" />
        <div className="space-y-2">
          <Shimmer className="h-8 w-48 max-w-full" />
          <Shimmer className="h-4 w-72 max-w-full" />
        </div>
      </div>
    </div>
    <div className="flex flex-wrap gap-2 md:gap-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <Shimmer key={i} className="h-9 flex-1 min-w-[100px] max-w-[140px] rounded-lg" />
      ))}
    </div>
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="px-6 py-4 border-b border-border">
        <Shimmer className="h-6 w-44" />
      </div>
      <div className="p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <Shimmer className="h-3 w-28" />
              <Shimmer className="h-10 w-full rounded-md" />
            </div>
          ))}
        </div>
        <div className="flex flex-wrap justify-between gap-3 pt-4 border-t border-border">
          <Shimmer className="h-9 w-28 rounded-md" />
          <Shimmer className="h-9 w-32 rounded-md" />
        </div>
      </div>
    </div>
  </div>
);

export const VehicleFormSkeleton: React.FC = () => (
  <div className="max-w-5xl mx-auto space-y-6">
    <div className="flex items-center space-x-4">
      <Shimmer className="h-9 w-24 rounded-md shrink-0" />
      <div className="space-y-2 min-w-0">
        <Shimmer className="h-8 w-40 max-w-full" />
        <Shimmer className="h-4 w-64 max-w-full" />
      </div>
    </div>
    <div className="flex flex-wrap gap-2 md:gap-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <Shimmer key={i} className="h-9 flex-1 min-w-[100px] max-w-[140px] rounded-lg" />
      ))}
    </div>
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="px-6 py-4 border-b border-border">
        <Shimmer className="h-6 w-48" />
      </div>
      <div className="p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <Shimmer className="h-3 w-28" />
              <Shimmer className="h-10 w-full rounded-md" />
            </div>
          ))}
        </div>
        <div className="flex flex-wrap justify-end gap-3 pt-4 border-t border-border">
          <Shimmer className="h-9 w-28 rounded-md" />
          <Shimmer className="h-9 w-32 rounded-md" />
        </div>
      </div>
    </div>
  </div>
);

// ── Main export ────────────────────────────────────────────────────────────────

export type ListPageSkeletonVariant = "table" | "cards" | "list";

interface ListPageSkeletonProps {
  /** Visual variant. Default: "table" */
  variant?: ListPageSkeletonVariant;
  /** Number of rows (table/list) or cards. Default: 6 */
  rows?: number;
  /** Show filter bar shimmer above the list. Default: true */
  showFilters?: boolean;
  /** Show stats bar shimmer above filters. Default: false */
  showStats?: boolean;
  /** Number of stats cards. Default: 4 */
  statsCount?: number;
}

export const ListPageSkeleton: React.FC<ListPageSkeletonProps> = ({
  variant = "table",
  rows = 6,
  showFilters = true,
  showStats = false,
  statsCount = 4,
}) => (
  <div className="space-y-5">
    {showStats && <StatsBarSkeleton count={statsCount} />}
    {showFilters && <FilterBarSkeleton />}
    {variant === "table" && <TableSkeleton rows={rows} />}
    {variant === "cards" && <CardGridSkeleton count={rows} />}
    {variant === "list"  && <ListRowsSkeleton rows={rows} />}
  </div>
);
